import React from 'react';
import { Shield, Lock, Eye, FileText, AlertCircle, CheckCircle2, Mail, Phone } from 'lucide-react';
import Header from "../Header";
import Footer from "../Footer";

export default function PrivacyPolicyPage({ isDarkMode, setIsDarkMode }) {
  const sections = [
    {
      id: 1,
      icon: FileText,
      title: "1. Informações que Coletamos",
      content: [
        {
          subtitle: "1.1 Informações Fornecidas por Si",
          text: "Coletamos informações que você nos fornece diretamente quando:",
          list: [
            "Preenche formulários de contacto no nosso website",
            "Solicita orçamentos ou informações sobre nossos serviços",
            "Subscreve a nossa newsletter",
            "Cria uma conta nos nossos sistemas",
            "Comunica connosco por email, telefone ou chat"
          ]
        },
        {
          subtitle: "1.2 Informações Coletadas Automaticamente",
          text: "Quando visita o nosso website, podemos coletar automaticamente:",
          list: [
            "Endereço IP e localização geográfica",
            "Tipo de navegador e dispositivo utilizado",
            "Páginas visitadas e tempo de permanência",
            "Links clicados e ações realizadas no site",
            "Data e hora de acesso"
          ]
        }
      ]
    },
    {
      id: 2,
      icon: Eye,
      title: "2. Como Utilizamos as Suas Informações",
      content: [
        {
          text: "Utilizamos as informações coletadas para:",
          list: [
            "Fornecer e melhorar nossos serviços",
            "Responder às suas solicitações e questões",
            "Enviar informações sobre produtos e serviços (com seu consentimento)",
            "Processar transações e pedidos",
            "Personalizar sua experiência no nosso website",
            "Cumprir obrigações legais e regulatórias",
            "Prevenir fraudes e garantir a segurança",
            "Realizar análises e pesquisas de mercado"
          ]
        }
      ]
    },
    {
      id: 3,
      icon: Lock,
      title: "3. Partilha de Informações",
      content: [
        {
          text: "A AngoNet não vende, aluga ou partilha as suas informações pessoais com terceiros, exceto nas seguintes situações:",
          list: [
            "Com seu consentimento expresso",
            "Com prestadores de serviços que nos auxiliam nas operações (sob acordo de confidencialidade)",
            "Quando exigido por lei ou ordem judicial",
            "Para proteger nossos direitos, propriedade ou segurança",
            "Em caso de fusão, aquisição ou venda de ativos da empresa"
          ]
        }
      ]
    },
    {
      id: 4,
      icon: Shield,
      title: "4. Segurança das Informações",
      content: [
        {
          text: "Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações:",
          list: [
            "Criptografia SSL/TLS para transmissão de dados",
            "Armazenamento seguro em servidores protegidos",
            "Controlo de acesso restrito aos dados pessoais",
            "Monitoramento contínuo de ameaças e vulnerabilidades",
            "Backups regulares e planos de recuperação de dados",
            "Treinamento de colaboradores sobre proteção de dados"
          ]
        }
      ]
    },
    {
      id: 5,
      icon: CheckCircle2,
      title: "5. Os Seus Direitos",
      content: [
        {
          text: "De acordo com a Lei de Protecção de Dados Pessoais de Angola, você tem o direito de:",
          list: [
            "Aceder aos seus dados pessoais que possuímos",
            "Corrigir informações incorretas ou desatualizadas",
            "Solicitar a eliminação dos seus dados",
            "Opor-se ao processamento dos seus dados",
            "Solicitar a portabilidade dos seus dados",
            "Retirar o consentimento a qualquer momento",
            "Apresentar reclamação junto da autoridade competente"
          ]
        }
      ]
    },
    {
      id: 6,
      icon: AlertCircle,
      title: "6. Cookies e Tecnologias Semelhantes",
      content: [
        {
          text: "O nosso website utiliza cookies para melhorar a sua experiência:",
          list: [
            "Cookies essenciais: Necessários para o funcionamento básico do site",
            "Cookies de desempenho: Ajudam-nos a entender como os visitantes usam o site",
            "Cookies de funcionalidade: Permitem personalizar a sua experiência",
            "Cookies de marketing: Utilizados para publicidade direcionada (com seu consentimento)"
          ]
        },
        {
          subtitle: "Gestão de Cookies",
          text: "Pode gerir ou desativar cookies através das configurações do seu navegador. Note que a desativação pode afetar a funcionalidade do website."
        }
      ]
    },
    {
      id: 7,
      icon: FileText,
      title: "7. Retenção de Dados",
      content: [
        {
          text: "Mantemos as suas informações pessoais apenas pelo tempo necessário para:",
          list: [
            "Cumprir os propósitos para os quais foram coletadas",
            "Satisfazer requisitos legais, contabilísticos ou de relatórios",
            "Resolver disputas e fazer cumprir nossos acordos",
            "Proteger nossos direitos legais"
          ]
        }
      ]
    },
    {
      id: 8,
      icon: Mail,
      title: "8. Contacto",
      content: [
        {
          text: "Para exercer os seus direitos ou esclarecer dúvidas sobre esta política, contacte-nos:",
          contact: {
            email: "geral@angonet.ao",
            phone: "+244 945 574 552",
            address: "Luanda, Município de Viana, Angola"
          }
        }
      ]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
    }`}>
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-[120px] animate-pulse ${
          isDarkMode ? 'bg-blue-500/10' : 'bg-blue-400/20'
        }`}></div>
        <div className={`absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-[120px] animate-pulse ${
          isDarkMode ? 'bg-purple-500/10' : 'bg-purple-500/20'
        }`} style={{animationDelay: '1s'}}></div>
      </div>

      <Header isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />

      {/* Hero Section */}
      <section className={`relative pt-32 pb-20 px-6 ${
        isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-blue-50 to-purple-50'
      }`}>
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-6">
            <Shield size={20} className="text-blue-600" />
            <span className={`text-sm font-semibold ${
              isDarkMode ? 'text-cyan-400' : 'text-blue-600'
            }`}>
              Privacidade e Proteção de Dados
            </span>
          </div>

          <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Política de <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Privacidade</span>
          </h1>
          
          <p className={`text-lg mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Última atualização: 14 de Fevereiro de 2026
          </p>

          <p className={`text-xl ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            A AngoNet está comprometida em proteger a sua privacidade e os seus dados pessoais. 
            Esta política explica como coletamos, usamos e protegemos as suas informações.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={section.id}
                className={`mb-12 p-8 rounded-2xl ${
                  isDarkMode ? 'bg-gray-800 border border-white/10' : 'bg-white border border-gray-200 shadow-lg'
                }`}
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                    <Icon size={24} className="text-white" />
                  </div>
                  <h2 className={`text-2xl md:text-3xl font-bold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {section.title}
                  </h2>
                </div>

                {section.content.map((item, idx) => (
                  <div key={idx} className="mb-6 last:mb-0">
                    {item.subtitle && (
                      <h3 className={`text-xl font-bold mb-3 ${
                        isDarkMode ? 'text-cyan-400' : 'text-blue-600'
                      }`}>
                        {item.subtitle}
                      </h3>
                    )}
                    
                    {item.text && (
                      <p className={`mb-4 leading-relaxed ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {item.text}
                      </p>
                    )}

                    {item.list && (
                      <ul className="space-y-3">
                        {item.list.map((listItem, listIdx) => (
                          <li key={listIdx} className="flex items-start space-x-3">
                            <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                              {listItem}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {item.contact && (
                      <div className={`mt-4 p-4 rounded-lg ${
                        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
                      }`}>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <Mail size={18} className={isDarkMode ? 'text-cyan-400' : 'text-blue-600'} />
                            <a
                              href={`mailto:${item.contact.email}`}
                              className={`hover:underline ${
                                isDarkMode ? 'text-cyan-400' : 'text-blue-600'
                              }`}
                            >
                              {item.contact.email}
                            </a>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Phone size={18} className={isDarkMode ? 'text-cyan-400' : 'text-blue-600'} />
                            <a
                              href={`tel:${item.contact.phone.replace(/\s/g, '')}`}
                              className={`hover:underline ${
                                isDarkMode ? 'text-cyan-400' : 'text-blue-600'
                              }`}
                            >
                              {item.contact.phone}
                            </a>
                          </div>
                          <div className="flex items-start space-x-3">
                            <AlertCircle size={18} className={`${isDarkMode ? 'text-cyan-400' : 'text-blue-600'} mt-0.5`} />
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                              {item.contact.address}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

          {/* Important Notice */}
          <div className={`p-6 rounded-xl border-2 ${
            isDarkMode
              ? 'bg-blue-500/10 border-cyan-500/30'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start space-x-3">
              <AlertCircle className={`flex-shrink-0 mt-1 ${
                isDarkMode ? 'text-cyan-400' : 'text-blue-600'
              }`} size={24} />
              <div>
                <h3 className={`text-lg font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Alterações a Esta Política
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Reservamo-nos o direito de atualizar esta Política de Privacidade periodicamente. 
                  Notificaremos sobre alterações significativas através do nosso website ou por email. 
                  A continuação do uso dos nossos serviços após tais alterações constitui a sua aceitação da política atualizada.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer isDarkMode={isDarkMode} />

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}